import type { Artist, Booking, CustomerRecord, Studio, WaiverStatus } from '@/domain/types';
import { format } from 'date-fns';
import { PRODUCT_SOCIAL_HANDLE, customerFacingSender } from '@/utils/brand';

export interface ConfirmationSmsMessage {
  /** What kind of message this is — used to colour-code and demo the sequence. */
  kind: 'confirm' | 'getting-here' | 'prep' | 'closer';
  body: string;
  /** Approximate cost per send (AUD). 4-7 segments depending on payload. */
  costAud: number;
}

function whatToCall(customer: CustomerRecord): string {
  return customer.preferredName?.trim() || customer.firstName;
}

function sessionDescriptor(booking: Booking): string {
  if (booking.sessionLength === 'small') return 'a small (~90 min) piece';
  if (booking.sessionLength === 'half-day') return 'a half-day session (~4 hours)';
  if (booking.sessionLength === 'full-day') return 'a full-day session (~8 hours)';
  return 'a multi-session piece';
}

function isLongSession(booking: Booking): boolean {
  return booking.sessionLength === 'half-day' || booking.sessionLength === 'full-day';
}

function timeOfDayCue(booking: Booking): string {
  const h = new Date(booking.start).getHours();
  if (h < 11) return 'morning';
  if (h < 15) return 'arvo';
  return 'evening';
}

function brandLine(studio: Studio): string {
  // Customer-facing: send from the product brand + location, not studio.brand.
  return customerFacingSender(studio);
}

/**
 * Build the 24h-out confirmation SMS thread for a booking. Each entry is a
 * separate message — the agent gets to see the whole sequence before
 * approving it for send.
 */
export function buildConfirmationThread(opts: {
  booking: Booking;
  customer: CustomerRecord;
  artist: Artist | undefined;
  studio: Studio;
}): ConfirmationSmsMessage[] {
  const { booking, customer, artist, studio } = opts;
  const name = whatToCall(customer);
  const artistName = artist ? artist.firstName : 'your artist';
  const timeLabel = format(new Date(booking.start), 'h:mma').toLowerCase();
  const dayLabel = format(new Date(booking.start), 'EEEE');

  const messages: ConfirmationSmsMessage[] = [];

  // 1) Confirmation prompt.
  messages.push({
    kind: 'confirm',
    body: `Hey ${name}, it's Sara from ${brandLine(studio)}. Quick confirm — you're booked with ${artistName} ${dayLabel.toLowerCase()} at ${timeLabel}. Reply YES to lock it in, NEW if you need a different time, or STOP to cancel (7-day deposit policy applies).`,
    costAud: 0.03,
  });

  // 2) Getting-here block. Combines address, transport, parking.
  const getting = studio.gettingHere;
  const gettingBits: string[] = [`We're at ${studio.address}, ${studio.postcode}.`];
  if (getting?.transport) gettingBits.push(getting.transport);
  if (getting?.parking) gettingBits.push(getting.parking);
  if (getting?.directions) gettingBits.push(getting.directions);
  messages.push({
    kind: 'getting-here',
    body: gettingBits.join(' '),
    costAud: 0.03,
  });

  // 3) Prep + what-to-expect.
  const prepBits: string[] = [];
  prepBits.push(`Your session: ${sessionDescriptor(booking)}.`);
  if (booking.designBrief) prepBits.push(`Brief: ${booking.designBrief}.`);
  if (isLongSession(booking) && studio.prepNotes?.eatBeforehand !== false) {
    prepBits.push('Eat a proper meal beforehand — long sessions need fuel.');
  }
  if (studio.prepNotes?.hydrate !== false) {
    prepBits.push('Hydrate today.');
  }
  if (studio.prepNotes?.custom) prepBits.push(studio.prepNotes.custom);
  prepBits.push(`Arrive 5-10 min early, ${timeOfDayCue(booking)} sessions fill quick.`);
  messages.push({
    kind: 'prep',
    body: prepBits.join(' '),
    costAud: 0.03,
  });

  // 4) Closer + alternative channels.
  messages.push({
    kind: 'closer',
    body: `Questions? Reply to this thread or DM us @${PRODUCT_SOCIAL_HANDLE} on Instagram. See you ${dayLabel.toLowerCase()}.`,
    costAud: 0.02,
  });

  return messages;
}

/** Total send cost for a single booking's full thread. */
export function threadCostAud(thread: ConfirmationSmsMessage[]): number {
  return thread.reduce((sum, m) => sum + m.costAud, 0);
}

// ---------------------------------------------------------------------------
// Confirmation cadence (CU 86d3eeupk / 86d3eeuzf). A booking is confirmed over
// exactly two pre-day messaging touchpoints - a 7-day call (with a waiver
// nudge folded in while it's outstanding) and a 2-day text reminder. The
// 7-day mark is also the deadline: if still unconfirmed, the spot can be
// refilled (see `confirmationDeadlineState`, unchanged).
// ---------------------------------------------------------------------------

// ASSUMPTION: 10-day + placement touches dropped in favour of the locked
// pre-day cadence (7-day call + 2-day text). Surface if tests disagree.
export type CadenceStage = 'seven-day' | 'two-day';

export interface CadenceStep {
  stage: CadenceStage;
  /** Human label for the timeline, e.g. "7 days out - call". */
  label: string;
  body: string;
  costAud: number;
  /** ISO instant this step is due, derived from the booking. */
  dueIso: string;
}

const CADENCE_LABEL: Record<CadenceStage, string> = {
  // L6 (17 Jul): the waiver dispatch moved off this step to its own T-4d track;
  // the 7-day CALL stays. The 7-day text still carries a "sign before the day"
  // nudge (with the link) when the waiver is outstanding.
  'seven-day': '7 days out - call',
  'two-day': '2 days out - text',
};

function minusDays(iso: string, days: number): string {
  return new Date(new Date(iso).getTime() - days * 24 * 60 * 60_000).toISOString();
}

/**
 * Build the full confirmation cadence for a booking. Returns one step per
 * touchpoint, with the ISO instant each is due so the screen can show what has
 * fired vs what's pending relative to the sim clock.
 */
export function buildConfirmationCadence(opts: {
  booking: Booking;
  customer: CustomerRecord;
  artist: Artist | undefined;
  studio: Studio;
  /**
   * Current waiver status for the booking. When provided and not yet `signed`,
   * the seven-day step folds in a "sign before the day" nudge with the link.
   * Actual dispatch timing is owned by I2/L6 (T-4d); this is cadence copy only.
   */
  waiverStatus?: WaiverStatus;
}): CadenceStep[] {
  const { booking, customer, artist, studio, waiverStatus } = opts;
  const name = whatToCall(customer);
  const artistName = artist ? artist.firstName : 'your artist';
  const timeLabel = format(new Date(booking.start), 'h:mma').toLowerCase();
  const dateLabel = format(new Date(booking.start), 'EEE d MMM');
  const dayLabel = format(new Date(booking.start), 'EEEE').toLowerCase();
  const waiverOutstanding = waiverStatus !== undefined && waiverStatus !== 'signed';

  const sevenBody = waiverOutstanding
    ? `${name}, your session with ${artistName} on ${dateLabel} at ${timeLabel} is a week away. Reply YES to confirm. Quick one: please sign your medical waiver before the day - it's 2 minutes: ${brandLine(studio)}/waiver.`
    : `${name}, your session with ${artistName} on ${dateLabel} at ${timeLabel} is a week away. Reply YES to confirm, or NEW if you need a different time.`;

  const twoBase = `See you ${dayLabel}! ${artistName} at ${timeLabel}. We're at ${studio.address}. Reply YES to confirm.`;
  const twoBody = waiverOutstanding
    ? `${twoBase} Also please sign your medical waiver before the day: ${brandLine(studio)}/waiver.`
    : twoBase;

  return [
    {
      stage: 'seven-day',
      label: CADENCE_LABEL['seven-day'],
      dueIso: minusDays(booking.start, 7),
      costAud: 0.03,
      body: sevenBody,
    },
    {
      stage: 'two-day',
      label: CADENCE_LABEL['two-day'],
      dueIso: minusDays(booking.start, 2),
      costAud: 0.02,
      body: twoBody,
    },
  ];
}

export type ConfirmationDeadlineState =
  | 'confirmed'
  | 'on-track'
  | 'deadline-approaching'
  | 'refillable';

/**
 * Where a booking sits in the confirmation cadence relative to `nowIso`.
 * Unconfirmed within 7 days → `refillable` (the spot can be offered to the
 * waitlist). Between 7 and 10 days → `deadline-approaching`. Earlier → `on-track`.
 */
export function confirmationDeadlineState(
  booking: Booking,
  nowIso: string,
): ConfirmationDeadlineState {
  if (booking.confirmedAt) return 'confirmed';
  const daysOut = (new Date(booking.start).getTime() - new Date(nowIso).getTime()) / 86_400_000;
  if (daysOut <= 7) return 'refillable';
  if (daysOut <= 10) return 'deadline-approaching';
  return 'on-track';
}
